import {
  Component,
  ChangeDetectionStrategy,
  ViewChild,
  ElementRef,
  ChangeDetectorRef,
  OnDestroy,
} from '@angular/core';
import {
  animate,
  AnimationEvent,
  state,
  style,
  transition,
  trigger,
} from '@angular/animations';
import { CdkPortalOutlet } from '@angular/cdk/portal';
import { FocusTrapFactory, FocusTrap } from '@angular/cdk/a11y';
import { DialogRef, DialogState } from './dialog-ref';
import { DialogConfig, DialogRole } from './dialog.types';

@Component({
  selector: 'app-dialog-container',
  templateUrl: './dialog-container.component.html',
  styleUrls: ['./dialog-container.component.scss'],
  changeDetection: ChangeDetectionStrategy.Default,
  host: {
    class: 'app-dialog-container',
    '[attr.role]': 'config.role || "dialog"',
    '[attr.aria-modal]': '"true"',
    '[attr.aria-labelledby]': 'ariaLabelledBy || undefined',
    '[attr.aria-label]': 'config?.ariaLabel || null',
    '[@dialogContainer]': 'animationState',
    '(@dialogContainer.done)': 'onAnimationDone($event)',
  },
  animations: [
    trigger('dialogContainer', [
      state('void, exit', style({ opacity: 0, transform: 'scale(0.7)' })),
      state('enter', style({ transform: 'none', opacity: 1 })),
      transition(
        '* => enter',
        animate(
          '150ms cubic-bezier(0, 0, 0.2, 1)',
          style({ transform: 'none', opacity: 1 })
        )
      ),
      transition(
        '* => void, * => exit',
        animate('75ms cubic-bezier(0.4, 0.0, 0.2, 1)', style({ opacity: 0 }))
      ),
    ]),
  ],
})
export class DialogContainerComponent implements OnDestroy {
  @ViewChild(CdkPortalOutlet, { static: true })
  portalOutlet!: CdkPortalOutlet;

  config!: DialogConfig;

  /** ID of the title element for aria-labelledby. Set by DialogTitleDirective. */
  ariaLabelledBy: string | null = null;

  private dialogRef!: DialogRef<any, any>;
  private focusTrap: FocusTrap | null = null;
  private elementFocusedBeforeOpen: HTMLElement | null = null;

  get animationState(): DialogState {
    return this.dialogRef?.containerState ?? 'void';
  }

  constructor(
    private readonly elementRef: ElementRef<HTMLElement>,
    private readonly focusTrapFactory: FocusTrapFactory,
    private readonly cdr: ChangeDetectorRef
  ) {}

  /** @internal Called by DialogService to wire the ref. */
  _init(dialogRef: DialogRef<any, any>, config: DialogConfig): void {
    this.dialogRef = dialogRef;
    this.config = config;
  }

  /** @internal Called after the component portal is attached. */
  _initFocusTrap(): void {
    this.elementFocusedBeforeOpen = document.activeElement as HTMLElement;

    this.focusTrap = this.focusTrapFactory.create(
      this.elementRef.nativeElement
    );

    this.trapFocus();
  }

  /** @internal Trigger change detection (e.g. after animation state change). */
  _detectChanges(): void {
    this.cdr.detectChanges();
  }

  onAnimationDone(event: AnimationEvent): void {
    if (event.toState === 'enter') {
      this.dialogRef._markOpened();
      this.trapFocus();
    }

    if (event.toState === 'exit' || event.toState === 'void') {
      this.restoreFocus();

      if (this.dialogRef.onAnimationDone) {
        this.dialogRef.onAnimationDone();
      }
    }
  }

  ngOnDestroy(): void {
    this.focusTrap?.destroy();
  }

  private trapFocus(): void {
    const autoFocus = this.config.autoFocus ?? 'first-tabbable';

    if (autoFocus === false || autoFocus === 'dialog') {
      this.elementRef.nativeElement.focus();
      return;
    }

    if (autoFocus === 'first-heading') {
      const heading = this.elementRef.nativeElement.querySelector<HTMLElement>(
        'h1, h2, h3, h4, h5, h6, [appDialogTitle]'
      );
      if (heading) {
        heading.focus();
        return;
      }
    }

    this.focusTrap?.focusInitialElementWhenReady().then((focused) => {
      if (!focused) {
        this.elementRef.nativeElement.focus();
      }
    });
  }

  private restoreFocus(): void {
    const toFocus = this.elementFocusedBeforeOpen;

    if (toFocus && typeof toFocus.focus === 'function') {
      const active = document.activeElement;
      const element = this.elementRef.nativeElement;

      if (
        !active ||
        active === document.body ||
        active === element ||
        element.contains(active)
      ) {
        toFocus.focus();
      }
    }

    this.elementFocusedBeforeOpen = null;
  }
}
